import { useContext, createContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import { useGetAccounts } from '~/services/api/auth';
import { memoryStorage } from '~/utils/localStorage';
import { isEmpty } from 'lodash';
import { showAlert } from '~/components/common/Alert';
import { errorAlert } from '~/components/common/Alert/alertParams';
import { useUnifiedAuthContext } from './UnifiedAuthProvider';

export interface AuthContextProps {
  hasAccount: boolean;
  isFetchingAccounts: boolean;
  accountInfo: {
    email?: string;
  };
  refetchAccounts: () => {};
}
export const AuthContext = createContext({} as AuthContextProps);

const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const { isVerifyingSSO } = useUnifiedAuthContext();

  const {
    data,
    isFetching: isFetchingAccounts,
    refetch,
  } = useGetAccounts({
    enabled: !isVerifyingSSO,
    onSuccess: async (resp: { email: string }) => {
      if (resp?.email) {
        memoryStorage.setAccount(resp);
      }
      return true;
    },
    onError: (error) => {
      const axiosError = error as AxiosError;
      const status = axiosError?.response?.status;
      if (status === 403) {
        showAlert(errorAlert('You do not have access to this system'));
      }
      navigate('/login');
    },
  });

  const hasAccount: boolean = useMemo(() => !isEmpty(data), [data]);

  const value = useMemo(
    () => ({
      hasAccount,
      accountInfo: data,
      isFetchingAccounts,
      refetchAccounts: refetch,
    }),
    [hasAccount, data, isFetchingAccounts, refetch],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('Components cannot be rendered outside the Auth component');
  }
  return context;
};

export { AuthProvider, useAuth };
